import { useEffect, useState } from "react";
import { samplePreview, type Hero, type PfpProject } from "@/pfp/generator";

export function HeroCard({
  hero,
  project,
  onUpdate,
  onRemove,
  onTokenIdChange,
}: {
  hero: Hero;
  project: PfpProject;
  onUpdate: (layerId: string, traitId: string) => void;
  onRemove: () => void;
  onTokenIdChange: (newId: number) => void;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  const pinnedKey = JSON.stringify(hero.pinned);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Card preview lives in a 120px square — 192px is more than enough.
      const result = await samplePreview(project, { hero, targetSize: 192 });
      if (!cancelled) setPreview(result?.dataUrl ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [project, hero, pinnedKey]);

  return (
    <div className="card flex flex-col gap-3 sm:flex-row">
      <div className="aspect-square w-full max-w-[120px] overflow-hidden rounded-md border border-border bg-muted">
        {preview ? (
          <img src={preview} alt={`Token #${hero.tokenId} preview`} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
            #{hero.tokenId}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Token #</span>
          <input
            type="number"
            min={1}
            className="input h-7 w-20"
            value={hero.tokenId}
            onChange={(e) => onTokenIdChange(Math.max(1, Number(e.target.value) || 1))}
          />
          <button className="btn-ghost ml-auto h-6 px-2 text-xs" onClick={onRemove} aria-label="Remove hero">
            ×
          </button>
        </div>
        <div className="grid gap-1.5 text-[11px]">
          {project.layers.map((l) => (
            <div key={l.id} className="grid grid-cols-[80px_1fr] items-center gap-2">
              <span className="truncate text-muted-foreground">{l.name}</span>
              <select
                className="input h-7 text-[11px]"
                value={hero.pinned[l.id] ?? ""}
                onChange={(e) => onUpdate(l.id, e.target.value)}
              >
                <option value="">(roll)</option>
                {l.traits.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
